import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, Table } from 'flowbite-react';
import { HiUsers, HiCollection, HiClock, HiDownload } from 'react-icons/hi';
import LoadingSpinner from '../../components/LoadingSpinner';

const DashboardStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${((import.meta.env.VITE_ENV == 'development') ? import.meta.env.VITE_BACKEND_LOCAL : import.meta.env.VITE_BACKEND)}/dashboard/stats`,
          { withCredentials: true }
        );
        setStats(response.data);
      } catch (err) {
        setError('Error al cargar estadísticas');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return <LoadingSpinner message="Cargando dashboard..." size="lg" color="blue" />;
  }

  if (error) {
    return <div className="text-red-500 p-4">{error}</div>;
  }

  if (!stats) {
    return null;
  }

  return (
    <div className="p-4 space-y-6">
      <h1 className="text-2xl font-semibold mb-4">Dashboard</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <div className="flex items-center">
            <HiUsers className="w-10 h-10 text-blue-500 mr-4" />
            <div>
              <h2 className="text-lg font-semibold mb-1">Usuarios</h2>
              <p className="text-sm text-gray-600">
                Total: <span className="font-bold">{stats.users.total}</span>
              </p>
              <p className="text-sm text-gray-600">
                Activos: <span className="font-bold text-green-600">{stats.users.active}</span> · Inactivos:{' '}
                <span className="font-bold text-red-600">{stats.users.inactive}</span>
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <HiCollection className="w-10 h-10 text-indigo-500 mr-4" />
            <div>
              <h2 className="text-lg font-semibold mb-1">Soluciones</h2>
              <p className="text-sm text-gray-600">
                Con archivos:{' '}
                <span className="font-bold">{stats.solutions.withFiles}</span>
              </p>
              <p className="text-sm text-gray-600">
                Soluciones sin archivos:{' '}
                <span className="font-bold">{stats.solutions.withoutFiles}</span>
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <HiClock className="w-10 h-10 text-amber-500 mr-4" />
            <div>
              <h2 className="text-lg font-semibold mb-1">Actividad reciente</h2>
              <p className="text-sm text-gray-600">
                Últimos logins y descargas en las tablas inferiores.
              </p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <h2 className="text-lg font-semibold mb-2">Últimas soluciones con archivos</h2>
          <Table>
            <Table.Head>
              <Table.HeadCell>Solución</Table.HeadCell>
              <Table.HeadCell>Descripción</Table.HeadCell>
              <Table.HeadCell>Último archivo</Table.HeadCell>
            </Table.Head>
            <Table.Body>
              {stats.solutions.lastWithFiles.map((s) => (
                <Table.Row key={s.solutionId}>
                  <Table.Cell>{s.name}</Table.Cell>
                  <Table.Cell>{s.description}</Table.Cell>
                  <Table.Cell>
                    {new Date(s.lastFileAt).toLocaleString()}
                  </Table.Cell>
                </Table.Row>
              ))}
              {stats.solutions.lastWithFiles.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={3} className="text-center text-gray-500">
                    Sin datos
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </Card>

        <div className="space-y-4">
          <Card>
            <h2 className="text-lg font-semibold mb-2">Últimos usuarios logueados</h2>
            <Table>
              <Table.Head>
                <Table.HeadCell>Usuario</Table.HeadCell>
                <Table.HeadCell>Email</Table.HeadCell>
                <Table.HeadCell>Fecha</Table.HeadCell>
              </Table.Head>
              <Table.Body>
                {stats.lastLogins.map((u, idx) => (
                  <Table.Row key={idx}>
                    <Table.Cell>{u.name}</Table.Cell>
                    <Table.Cell>{u.email}</Table.Cell>
                    <Table.Cell>
                      {new Date(u.date).toLocaleString()}
                    </Table.Cell>
                  </Table.Row>
                ))}
                {stats.lastLogins.length === 0 && (
                  <Table.Row>
                    <Table.Cell colSpan={3} className="text-center text-gray-500">
                      Sin datos
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </Card>

          <Card>
            <div className="flex items-center mb-2">
              <HiDownload className="w-6 h-6 text-emerald-500 mr-2" />
              <h2 className="text-lg font-semibold">Últimas descargas de recursos</h2>
            </div>
            <Table>
              <Table.Head>
                <Table.HeadCell>Recurso</Table.HeadCell>
                <Table.HeadCell>Área</Table.HeadCell>
                <Table.HeadCell>Usuario</Table.HeadCell>
                <Table.HeadCell>Fecha</Table.HeadCell>
              </Table.Head>
              <Table.Body>
                {stats.lastResourceDownloads.map((d, idx) => (
                  <Table.Row key={idx}>
                    <Table.Cell>{d.fileName || '-'}</Table.Cell>
                    <Table.Cell>{d.areaName || '-'}</Table.Cell>
                    <Table.Cell>
                      {d.userName}{' '}
                      <span className="text-xs text-gray-500">
                        {d.userEmail ? `(${d.userEmail})` : ''}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      {new Date(d.date).toLocaleString()}
                    </Table.Cell>
                  </Table.Row>
                ))}
                {stats.lastResourceDownloads.length === 0 && (
                  <Table.Row>
                    <Table.Cell colSpan={4} className="text-center text-gray-500">
                      Sin datos
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DashboardStats;
